import React, { Component, useEffect } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import Body from '../Components/layout/Body';
import { extractActivityButtons } from './ConstActivityBtns';
import { GOALS } from './ConstGoalValues';
import { SUBTABS, GOALS_STEPS } from './ConstPaths';
import { getOtherEntryFromArray, OTHER_CKBX_VAL } from './OtherCheckboxUtils';

const PRIORITY_HELP = `
  <div>
    <p><strong>Purpose:</strong> If your client selected multiple activities, it's helpful to choose one as the priority for now. 
    This can help focus your work together and clarify a definite starting point.</p>
    <p>Eventually, of course, you’ll want to define access solutions that support all the client’s desired activities.  
    But focusing on one activity at a time can simplify your thinking as a team.  
    Throughout the process, you can focus on the question of whether or not a particular choice supports the priority activity.</p>
    <p><strong>Example:</strong> Suppose the client chooses two activities: ‘play mainstream computer games’ and ‘write messages or documents’. 
    The type of access solution you’d need for computer games may be quite different than what you’d need for writing.  
    So it makes some sense to choose one as the priority activity, then come back and focus on the other, then integrate the two.</p>
    <p><strong>Note:</strong> If you choose a priority activity that is in the 'low/no-tech' group, you'll be on the 'low-tech' pathway. This just means that we'll try to word things in a way that matches a low-tech goal, and try not to ask you questions that relate to higher-tech situations.</p>
    <p><strong>Low-tech Example:</strong> In the low-tech case, if the client needs to signal yes and no, and also needs to signal for attention, you'll want to try to identify distinct physical access methods for these, for clarity. So choose one of those as the priority activity to start with, then work on the other goal when ready.</p>
  </div>
`;

const propTypes = {
  firstName: PropTypes.string,
  mainGoal: PropTypes.string,
  activities: PropTypes.arrayOf(PropTypes.string),
  priority: PropTypes.string,
  onChangePriority: PropTypes.func.isRequired,
  saveFormData: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  mainGoal: GOALS.FULL,
  activities: [],
  priority: ''
};

//content sub-component with unique content for this page
class GoalsPriorityContent extends Component {
  constructor(props){
    super(props);
  }

  getActiveBtns(chosenActivities, allBtns){
    //chosenActivities is array of string values for the chosen activities
    //allBtns is array of config for all available activities
    var activeBtns=[];
    allBtns.forEach((btn)=>{
      let matchIndex = chosenActivities.findIndex((a=>a.startsWith(btn.value)));
      //store the *activity string* as the button value, so it includes the full 'other' entries.
      //for non-other activities, the activity is the btn.value,
      //for 'other', the activity includes 'other - ' plus free text, like 'other - that great activity'
      if ( matchIndex > -1 ){
        activeBtns.push({value: chosenActivities[matchIndex], label: btn.label, moreInfo: btn.moreInfo});
	    }
    });
    return activeBtns;
  }

  //Render the form.
  //Calls the setter that's passed in by the parent, when a radio button is clicked.
  //That setter changes the state of the priority activity in the parent, 
  //which triggers a new render of the view with the new priority activity.

  render(){
    const mainGoal = this.props.mainGoal;
    const activities = this.props.activities;
    const priority = this.props.priority;
    const onChangePriority = this.props.onChangePriority;
    const addPath = SUBTABS.GOALS + "/" + GOALS_STEPS.ACTIVITIES;

    console.log("rendering GatherInfoGoalsPriority Content, activities: " + activities);

    let availableBtns = extractActivityButtons();
    let activeBtns = [];
    activeBtns = this.getActiveBtns(activities, availableBtns);

    //just in case we get to this view without any activities:
    if(activities.length === 0){
      return(
        <>
        <div className="aa-callout">
          <p><strong>Note:</strong> There are no options for a priority activity here because you haven't selected any activities yet.</p>
          <p>You can:</p>
          <ul>
            <li>Click Continue to continue without a priority activity.</li>
            <li>Or <Link to={addPath}>add some activities</Link> and come back here to select a priority activity.</li>
          </ul>
        </div>
        </>
      );
    }
    else{

        return(
        <>
        <Form>
          <FormGroup tag="fieldset">
            {activeBtns.map((btn, index) =>
              <FormGroup check key={index}>
                <Input
                  id={btn.value}
                  value={btn.value}
                  checked={(priority===btn.value)}
                  onChange={onChangePriority}
                  name="priority"
                  type="radio"
                />
                {' '}
                <Label for={btn.value} check>
                  {btn.label}
                  {' '}
                  { ((btn.value).startsWith(OTHER_CKBX_VAL))
                    ? <span className="aa-more-info">{getOtherEntryFromArray(activities)}</span>
                    : <span className="aa-more-info">{btn.moreInfo}</span>
                  }
                </Label>
              </FormGroup>
            )}
          </FormGroup>
        </Form>
        {/* {(activities.length === 1) ? <p className="aa-more-info">NOTE: With only one activity, it's automatically the priority.</p> : <span></span>} */}
        </>
        );
    }
  }
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component.
//since the main goal step includes a form, receive the initial form values via props,
//along with setters to change those values based on the form input.
function GatherInfoGoalsPriority(props) {
  const firstName = props.firstName;
  const mainGoal = props.mainGoal;
  const activities = props.activities;
  const priority = props.priority;
  const onChangePriority = props.onChangePriority;
  const saveFormData = props.saveFormData;
  const onContinue = props.onContinue;

  console.log("rendering GatherInfoGoalsPriority, priority: " + priority);

  const pageData = {
    image: "", //not yet used
    faIcon: "",
    pageSymbolClassName: "aa-section-symbol gather",
    title: `Of the activities you identified, pick one with ${firstName} to focus on now.`,
    subTitle: "Picking a priority activity will help focus your work together and gauge progress more clearly. " +  
      "This might be the most motivating activity, or one that seems most attainable in the short term. " +
      "You can always move on to a different activity at any time.",
    learnMore: {linkText: "Learn More about picking a priority activity",
                title: "Priority Activity",
                content: PRIORITY_HELP
                },
    nextRoute: "", //not used since we have an onContinue callback
    sidebar: true
  }

  useEffect(() => {
    console.log('GIGPriority mounted');
    return () => {
      console.log('GIGPriority unmounting and saving...');
      saveFormData();
    }
  }, [saveFormData])

  return(
    <>
    <Body page={pageData} 
      contentComponent={<GoalsPriorityContent mainGoal={mainGoal} activities={activities} priority={priority} onChangePriority={onChangePriority}/>}
      onContinue={onContinue} 
    />
    </>
  );
}

GatherInfoGoalsPriority.propTypes = propTypes;
GatherInfoGoalsPriority.defaultProps = defaultProps;

export default GatherInfoGoalsPriority;